import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatWIB } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ArchiveButton } from "@/components/ArchiveButton";
import { Users2, Plus, X, CheckCircle2, ArrowRight, ListTodo, AlertOctagon, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const toIso = (v) => (v ? new Date(v + ":00+07:00").toISOString() : null);
const MTYPE = { mingguan: "Mingguan", bulanan: "Bulanan", ad_hoc: "Ad-hoc" };
const MSTATUS = { dijadwalkan: "bg-slate-100 text-slate-600", berlangsung: "bg-sky-100 text-sky-700", selesai: "bg-emerald-100 text-emerald-700" };

function MeetingDetail({ mid, open, onOpenChange, positions, onChanged }) {
  const [m, setM] = useState(null);
  const [showIssue, setShowIssue] = useState(false);
  const [iTitle, setITitle] = useState("");
  const [iDesc, setIDesc] = useState("");
  const [solutions, setSolutions] = useState([""]);
  const [actions, setActions] = useState([{ description: "", assignee_position_id: "", deadline: "" }]);

  const load = () => api.get(`/meetings/${mid}`).then((r) => setM(r.data));
  useEffect(() => { if (open && mid) load(); }, [open, mid]);
  if (!m) return <Sheet open={open} onOpenChange={onOpenChange}><SheetContent className="w-full sm:max-w-2xl" aria-describedby={undefined}><SheetHeader className="sr-only"><SheetTitle>Memuat rapat</SheetTitle></SheetHeader><div className="p-6 text-slate-400">Memuat...</div></SheetContent></Sheet>;

  const setStatus = async (s) => { await api.post(`/meetings/${mid}/status?status=${s}`); load(); onChanged?.(); };
  const addIssue = async () => {
    if (!iTitle.trim()) return toast.error("Judul masalah wajib");
    await api.post(`/meetings/${mid}/issues`, {
      title: iTitle, description: iDesc, solutions: solutions.filter((s) => s.trim()),
      actions: actions.filter((a) => a.description.trim()).map((a) => ({ ...a, deadline: toIso(a.deadline) || null })),
    });
    toast.success("Masalah ditambahkan"); setShowIssue(false); setITitle(""); setIDesc(""); setSolutions([""]);
    setActions([{ description: "", assignee_position_id: "", deadline: "" }]); load(); onChanged?.();
  };
  const resolveIssue = async (iid) => { await api.post(`/meetings/${mid}/issues/${iid}/resolve`); load(); onChanged?.(); };
  const toTask = async (iid, aid) => {
    try { await api.post(`/meetings/${mid}/issues/${iid}/actions/${aid}/to-task`); toast.success("Aksi menjadi tugas & ditugaskan"); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto zoa-scroll" data-testid="meeting-detail" aria-describedby={undefined}>
        <SheetHeader><SheetTitle className="text-left">{m.title}</SheetTitle></SheetHeader>
        <div className="flex items-center gap-2 mt-2 flex-wrap text-xs">
          <span className={cn("rounded-full px-2 py-0.5 font-semibold", MSTATUS[m.status])}>{m.status}</span>
          <span className="text-slate-400">{MTYPE[m.type]} · {formatWIB(m.scheduled_at)}</span>
          {m.attendee_names?.length > 0 && <span className="text-slate-400">· {m.attendee_names.filter(Boolean).join(", ")}</span>}
        </div>
        <div className="flex gap-2 mt-3">
          {m.status !== "berlangsung" && <Button size="sm" variant="outline" onClick={() => setStatus("berlangsung")} data-testid="meeting-start-btn">Mulai Rapat</Button>}
          {m.status !== "selesai" && <Button size="sm" variant="outline" onClick={() => setStatus("selesai")} data-testid="meeting-close-btn">Selesaikan</Button>}
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1"><AlertOctagon className="h-3.5 w-3.5" />Masalah → Solusi → Aksi</p>
            <Button size="sm" onClick={() => setShowIssue((v) => !v)} data-testid="add-issue-btn"><Plus className="h-4 w-4 mr-1" />Masalah</Button>
          </div>

          {showIssue && (
            <div className="rounded-xl border border-slate-200 p-3 mb-3 space-y-2">
              <Input placeholder="Judul masalah" value={iTitle} onChange={(e) => setITitle(e.target.value)} data-testid="issue-title-input" />
              <Textarea placeholder="Deskripsi" value={iDesc} onChange={(e) => setIDesc(e.target.value)} rows={2} />
              <Label className="text-xs">Solusi</Label>
              {solutions.map((s, i) => (
                <div key={i} className="flex gap-2"><Input value={s} onChange={(e) => setSolutions((a) => a.map((x, j) => j === i ? e.target.value : x))} placeholder={`Solusi ${i + 1}`} />{solutions.length > 1 && <Button variant="ghost" size="icon" onClick={() => setSolutions((a) => a.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}</div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setSolutions((a) => [...a, ""])}><Plus className="h-3.5 w-3.5 mr-1" />Solusi</Button>
              <Label className="text-xs block mt-2">Rencana Aksi (bisa jadi tugas)</Label>
              {actions.map((a, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input value={a.description} onChange={(e) => setActions((arr) => arr.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} placeholder="Aksi" />
                  <Select value={a.assignee_position_id} onValueChange={(v) => setActions((arr) => arr.map((x, j) => j === i ? { ...x, assignee_position_id: v } : x))}>
                    <SelectTrigger className="w-40"><SelectValue placeholder="PIC" /></SelectTrigger>
                    <SelectContent>{positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="datetime-local" value={a.deadline} onChange={(e) => setActions((arr) => arr.map((x, j) => j === i ? { ...x, deadline: e.target.value } : x))} className="w-44" />
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setActions((a) => [...a, { description: "", assignee_position_id: "", deadline: "" }])}><Plus className="h-3.5 w-3.5 mr-1" />Aksi</Button>
              <div><Button size="sm" onClick={addIssue} data-testid="issue-save-btn" className="bg-indigo-600 hover:bg-indigo-700 mt-1">Simpan Masalah</Button></div>
            </div>
          )}

          <div className="space-y-3">
            {m.issues.map((iss) => (
              <div key={iss.id} className={cn("rounded-xl border p-3", iss.status === "selesai" ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200")} data-testid={`issue-${iss.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{iss.title}
                      {iss.carried_from_meeting_id && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">CARRY-OVER</span>}</p>
                    {iss.description && <p className="text-xs text-slate-500 mt-0.5">{iss.description}</p>}
                  </div>
                  {iss.status !== "selesai" && <Button size="sm" variant="ghost" className="text-emerald-600 h-7" onClick={() => resolveIssue(iss.id)} data-testid={`resolve-issue-${iss.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Selesai</Button>}
                </div>
                {iss.solutions?.length > 0 && <div className="mt-2 text-xs text-slate-600">💡 {iss.solutions.map((s) => s.text).join(" · ")}</div>}
                {iss.actions?.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {iss.actions.map((a) => (
                      <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs">
                        <span className="text-slate-700"><ListTodo className="h-3.5 w-3.5 inline mr-1 text-indigo-500" />{a.description} <span className="text-slate-400">→ {a.assignee_title || "belum ada PIC"}</span></span>
                        {a.task_id ? <span className="text-emerald-600 font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Jadi Tugas</span>
                          : <Button size="sm" className="h-6 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={() => toTask(iss.id, a.id)} data-testid={`action-to-task-${a.id}`}>Jadikan Tugas <ArrowRight className="h-3 w-3 ml-1" /></Button>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {m.issues.length === 0 && <p className="text-sm text-slate-400 italic">Belum ada masalah dibahas.</p>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function OpenIssuesRegistry() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/meetings/issues/open").then((r) => setItems(r.data.items)); }, []);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="open-issues-registry">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr><th className="text-left px-4 py-2.5">Masalah</th><th className="text-left px-3">Rapat</th><th className="text-center px-3">Aksi</th><th className="text-center px-3">Jadi Tugas</th><th className="text-center px-3">Status</th></tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.issue_id} className="border-t border-slate-100">
              <td className="px-4 py-2.5 font-medium text-slate-700">{it.title}</td>
              <td className="px-3 text-slate-500">{it.meeting_title}<br /><span className="text-xs text-slate-400">{formatWIB(it.scheduled_at, false)}</span></td>
              <td className="text-center">{it.actions}</td>
              <td className="text-center text-emerald-600">{it.converted}</td>
              <td className="text-center">{it.carried ? <span className="text-xs text-amber-600 font-bold">Carry-over</span> : <span className="text-xs text-slate-400">Terbuka</span>}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-8">Tidak ada masalah terbuka. 🎉</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Meetings() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [positions, setPositions] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "mingguan", scheduled_at: "", division_id: "", branch_id: "" });

  const load = () => api.get("/meetings").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/org/positions").then((r) => setPositions(r.data));
    api.get("/divisions").then((r) => setDivisions(r.data));
    api.get("/branches").then((r) => setBranches(r.data));
  }, []);

  const create = async () => {
    if (!form.title.trim() || !form.scheduled_at) return toast.error("Judul & jadwal wajib");
    await api.post("/meetings", { ...form, scheduled_at: toIso(form.scheduled_at), attendees: [] });
    toast.success("Rapat dibuat (masalah belum selesai dari rapat sebelumnya otomatis dibawa)");
    setCreateOpen(false); setForm({ title: "", type: "mingguan", scheduled_at: "", division_id: "", branch_id: "" }); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Users2 className="h-7 w-7 text-indigo-500" />Rapat</h1>
          <p className="text-slate-500 text-sm">Masalah → Solusi → Aksi jadi tugas, dengan carry-over agenda belum tuntas</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-meeting-btn" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1.5" />Buat Rapat</Button>
      </div>

      <Tabs defaultValue="rapat">
        <TabsList className="mb-5">
          <TabsTrigger value="rapat" data-testid="meetings-tab-list"><CalendarClock className="h-4 w-4 mr-1.5" />Daftar Rapat</TabsTrigger>
          <TabsTrigger value="masalah" data-testid="meetings-tab-issues"><AlertOctagon className="h-4 w-4 mr-1.5" />Daftar Masalah</TabsTrigger>
        </TabsList>
        <TabsContent value="rapat">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((m) => (
              <div key={m.id} onClick={() => { setOpenId(m.id); setDrawer(true); }} data-testid={`meeting-card-${m.id}`}
                className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:-translate-y-0.5 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-bold text-slate-800 leading-snug">{m.title}</p>
                  <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold", MSTATUS[m.status])}>{m.status}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{MTYPE[m.type]} · {formatWIB(m.scheduled_at, false)}</p>
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-3 pt-2 border-t border-slate-100">
                  <span>{m.stats.issues} masalah</span>
                  <span className="text-amber-600">{m.stats.open_issues} terbuka</span>
                  <span className="text-emerald-600">{m.stats.actions_converted}/{m.stats.actions} aksi→tugas</span>
                  <ArchiveButton url={`/meetings/${m.id}`} label="rapat ini" onDone={load} testid={`archive-meeting-${m.id}`} className="ml-auto" />
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">Belum ada rapat.</p>}
          </div>
        </TabsContent>
        <TabsContent value="masalah"><OpenIssuesRegistry /></TabsContent>
      </Tabs>

      <MeetingDetail mid={openId} open={drawer} onOpenChange={setDrawer} positions={positions} onChanged={load} />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Buat Rapat Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Judul</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="meeting-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jenis</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="meeting-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="mingguan">Mingguan</SelectItem><SelectItem value="bulanan">Bulanan</SelectItem><SelectItem value="ad_hoc">Ad-hoc</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Jadwal</Label><Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))} data-testid="meeting-schedule-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Divisi</Label>
                <Select value={form.division_id} onValueChange={(v) => setForm((f) => ({ ...f, division_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{divisions.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Cabang</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm((f) => ({ ...f, branch_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Batal</Button><Button onClick={create} data-testid="meeting-create-submit" className="bg-indigo-600 hover:bg-indigo-700">Buat</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
